import { z } from 'zod';

import { apiRequest } from '@/api/client';
import { pageSchema } from '@/api/types';

/**
 * Cancellations of some lines, returns, refunds and replacements. Mirrors the
 * request schemas in `app/schemas/order.py`.
 */

export const REQUEST_KINDS = ['CANCELLATION', 'RETURN', 'REFUND', 'REPLACEMENT'] as const;
export type RequestKind = (typeof REQUEST_KINDS)[number];

export const REQUEST_STATUSES = ['REQUESTED', 'APPROVED', 'REJECTED', 'COMPLETED'] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

/** Kinds whose approval also carries them out - nothing physical to wait for. */
export const COMPLETE_ON_APPROVAL: readonly RequestKind[] = ['CANCELLATION', 'REFUND'];

/** Kinds that send money back when they complete. */
export const REFUNDING_KINDS: readonly RequestKind[] = ['CANCELLATION', 'RETURN', 'REFUND'];

export const orderRequestSchema = z.object({
  id: z.string(),
  order_id: z.string(),
  kind: z.enum(REQUEST_KINDS),
  status: z.enum(REQUEST_STATUSES),
  requested_by: z.enum(['CUSTOMER', 'MERCHANT']),
  reason: z.string(),
  comment: z.string().nullish(),
  photos: z.array(z.string()).default([]),
  resolution_note: z.string().nullish(),
  refund_amount: z.string().nullish(),
  refund_method: z.string().nullish(),
  refund_reference: z.string().nullish(),
  created_at: z.string(),
  approved_at: z.string().nullish(),
  rejected_at: z.string().nullish(),
  completed_at: z.string().nullish(),
  items: z
    .array(
      z.object({
        order_item_id: z.string(),
        product_name: z.string(),
        variant_options: z.record(z.unknown()).default({}),
        sku: z.string().default(''),
        quantity: z.number(),
      }),
    )
    .default([]),
});

export type OrderRequest = z.infer<typeof orderRequestSchema>;

export const orderRequestListItemSchema = orderRequestSchema.extend({
  order_number: z.string(),
  order_status: z.string(),
  email: z.string(),
  customer_name: z.string().nullish(),
  suggested_refund: z.string().default('0.00'),
});

export type OrderRequestListItem = z.infer<typeof orderRequestListItemSchema>;

export const orderRequestCountsSchema = z.object({
  open: z.number(),
  by_status: z.record(z.number()),
});

export interface OrderRequestListParams {
  page?: number;
  size?: number;
  status?: RequestStatus[];
  kind?: RequestKind;
}

export function fetchOrderRequests(params: OrderRequestListParams = {}) {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.size) query.set('size', String(params.size));
  for (const status of params.status ?? []) query.append('status', status);
  if (params.kind) query.set('kind', params.kind);
  const suffix = query.toString() ? `?${query}` : '';
  return apiRequest(`/order-requests${suffix}`, pageSchema(orderRequestListItemSchema));
}

export function fetchOrderRequestCounts() {
  return apiRequest('/order-requests/counts', orderRequestCountsSchema);
}

export interface RequestDecision {
  /** Shown to the shopper. */
  note?: string;
  /** Overrides the suggested refund, when the step sends one. */
  refund_amount?: string;
}

export function approveOrderRequest(id: string, decision: RequestDecision = {}) {
  return apiRequest(`/order-requests/${id}/approve`, orderRequestSchema, {
    method: 'POST',
    body: decision,
  });
}

export function rejectOrderRequest(id: string, note: string) {
  return apiRequest(`/order-requests/${id}/reject`, orderRequestSchema, {
    method: 'POST',
    body: { note },
  });
}

export function completeOrderRequest(id: string, decision: RequestDecision = {}) {
  return apiRequest(`/order-requests/${id}/complete`, orderRequestSchema, {
    method: 'POST',
    body: decision,
  });
}

/**
 * The lines' share of what the shopper paid, as the server suggests it:
 * discounts spread by value, delivery and a COD fee left out.
 */
export function suggestedRefund(
  order: { subtotal: string; total: string; shipping_amount: string; payment_fee_amount?: string },
  lines: { line_subtotal: string; quantity: number }[],
  requested: number[],
): string {
  const subtotal = Number(order.subtotal);
  if (!(subtotal > 0)) return '0.00';
  const goodsPaid =
    Number(order.total) - Number(order.shipping_amount) - Number(order.payment_fee_amount ?? 0);
  const share = lines.reduce(
    (sum, line, index) =>
      line.quantity > 0 ? sum + (Number(line.line_subtotal) * (requested[index] ?? 0)) / line.quantity : sum,
    0,
  );
  return Math.max((goodsPaid * share) / subtotal, 0).toFixed(2);
}
